import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { getSizeCharts } from "@/lib/api";

let cache;
const useCharts = (open) => {
  const [c, setC] = useState(cache);
  useEffect(() => { if (open && !cache) getSizeCharts().then((r) => { cache = r; setC(r); }).catch(() => {}); }, [open]);
  return c;
};
export const resetSizeChartCache = () => { cache = undefined; };

const MeasureArt = () => (
  <svg viewBox="0 0 220 200" className="h-auto w-full max-w-[220px]" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-label="How to measure a tee: chest across, length from shoulder to hem, sleeve from shoulder seam to cuff" role="img">
    <path d="M70 20 L40 30 L10 65 L35 85 L50 70 L50 185 L170 185 L170 70 L185 85 L210 65 L180 30 L150 20 Q110 45 70 20 Z" fill="#F4F3EC" />
    <path d="M58 92 H162" stroke="#FF7EC7" strokeWidth="3" />
    <path d="M58 86 V98 M162 86 V98" stroke="#FF7EC7" strokeWidth="3" />
    <path d="M110 34 V180" stroke="#7FD3FF" strokeWidth="3" strokeDasharray="1 0" />
    <path d="M104 34 H116 M104 180 H116" stroke="#7FD3FF" strokeWidth="3" />
    <path d="M150 22 L198 56" stroke="#9BC400" strokeWidth="3" />
    <text x="66" y="110" fill="#111" stroke="none" fontFamily="Space Mono, monospace" fontSize="10">A chest</text>
    <text x="114" y="150" fill="#111" stroke="none" fontFamily="Space Mono, monospace" fontSize="10">B length</text>
    <text x="150" y="16" fill="#111" stroke="none" fontFamily="Space Mono, monospace" fontSize="10">C sleeve</text>
  </svg>
);

const fmt = (cm, unit) => (unit === "cm" ? Math.round(cm) : (cm / 2.54).toFixed(1));

export const SizeGuide = ({ category, open, onOpenChange }) => {
  const charts = useCharts(open);
  const [unit, setUnit] = useState("cm");
  const c = charts?.[category];
  const oneSize = !c?.rows?.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-none border-[1.5px] border-ink bg-cloud p-0" data-testid="size-guide-modal">
        <div className="border-b-[1.5px] border-ink bg-sky px-6 py-6 sm:px-8">
          <DialogTitle className="display text-5xl">Size guide</DialogTitle>
          <DialogDescription className="label mt-2 font-bold text-ink">{category} · measured flat</DialogDescription>
        </div>
        {!c ? <p className="label p-8 text-ink/60">Loading…</p> : (
          <div className="grid gap-8 p-6 sm:p-8 md:grid-cols-[1fr_220px]">
            <div>
              {oneSize ? (
                <div data-testid="size-guide-one-size"><p className="display text-4xl">One size fits most</p><p className="mt-4 text-[15px] text-ink/75" data-testid="size-guide-dimensions">{c.dimensions}</p></div>
              ) : (
                <>
                  <div className="flex border-[1.5px] border-ink" role="group" aria-label="Units" style={{ width: "fit-content" }}>
                    {["cm", "in"].map((u) => <button key={u} onClick={() => setUnit(u)} aria-pressed={unit === u} className={`label px-4 py-2 font-bold ${unit === u ? "bg-ink text-cloud" : "hover:bg-lime"}`} data-testid={`size-guide-unit-${u}`}>{u}</button>)}
                  </div>
                  <table className="mt-5 w-full text-left text-sm" data-testid="size-guide-table">
                    <thead><tr className="label border-b border-ink text-ink/70"><th className="py-2">Size</th><th>A Chest</th><th>B Length</th><th>C Sleeve</th></tr></thead>
                    <tbody>{c.rows.map((r) => (
                      <tr key={r.size} className="border-b border-ink/10" data-testid={`size-guide-row-${r.size}`}><td className="py-2.5 font-mono font-bold">{r.size}</td><td>{fmt(r.chest, unit)}</td><td>{fmt(r.length, unit)}</td><td>{fmt(r.sleeve, unit)}</td></tr>
                    ))}</tbody>
                  </table>
                </>
              )}
              <p className="mt-6 bg-lime px-4 py-3 text-[14px]" data-testid="size-guide-fit-note">{c.fit_note}</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <MeasureArt />
              <p className="text-center text-[12px] leading-relaxed text-ink/65">Lay a tee you love flat. A: armpit to armpit. B: highest shoulder point to hem. C: shoulder seam to cuff.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
